import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiPackage, FiX, FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const DrugModal = ({ isOpen, onClose, drugs, meta, onPageChange, onSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setSearchTerm("");
  }, [isOpen]);

  if (!isOpen) return null;

  const drugList = Array.isArray(drugs) ? drugs : [];
  const currentPage = meta?.current_page || 1;
  const lastPage = meta?.last_page || 1;
  const totalItems = meta?.total ?? drugList.length;
  const perPage = meta?.per_page || 10;

  const startIndex = (currentPage - 1) * perPage;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (onSearch) {
      onSearch(val);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]"
        >
          {/* Header Modal */}
          <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                <FiPackage className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Daftar Stok Obat Outlet</h2>
                <p className="text-xs text-gray-500">Total {totalItems} jenis obat terdaftar</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <div className="relative">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Cari nama obat atau produsen..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          {/* Table Container */}
          <div className="p-4 overflow-y-auto flex-1">
            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
              <table className="w-full text-left text-xs text-gray-600">
                <thead>
                  <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                    <th className="p-3 w-12 text-center">No</th>
                    <th className="p-3">Nama Obat</th>
                    <th className="p-3">Produsen</th>
                    <th className="p-3 text-center">Stok</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {drugList.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-10 text-center text-gray-400 font-medium">
                        Tidak ada data obat ditemukan
                      </td>
                    </tr>
                  ) : (
                    drugList.map((drug, idx) => {
                      const itemNumber = startIndex + idx + 1;
                      const drugName = drug.name || drug.nama_obat || drug.drug_name || "Obat";
                      const manufacturer = drug.produsen || drug.manufacturer || drug.distributor || "-";
                      const stockVal = drug.stock ?? drug.stok ?? drug.qty ?? 0;
                      return (
                        <tr key={drug.id || idx} className="hover:bg-emerald-50/30 transition-colors">
                          <td className="p-3 text-center font-semibold text-gray-400">
                            {itemNumber}
                          </td>
                          <td className="p-3 font-bold text-gray-900 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                              💊
                            </div>
                            <span>{drugName}</span>
                          </td>
                          <td className="p-3 text-gray-600">
                            {manufacturer}
                          </td>
                          <td className="p-3 text-center">
                            <span
                              className={`inline-flex px-2.5 py-0.5 rounded-lg text-[11px] font-extrabold ${
                                stockVal > 10
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : stockVal > 0
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-rose-50 text-rose-700 border border-rose-200"
                              }`}
                            >
                              {stockVal} unit
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Modal & Server Pagination Controls */}
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50/50 text-xs">
            <p className="text-gray-500 font-medium">
              Halaman <span className="font-bold text-gray-800">{currentPage}</span> dari <span className="font-bold text-gray-800">{lastPage}</span> (Total <span className="font-bold text-gray-800">{totalItems}</span> obat)
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className={`p-1.5 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                  currentPage <= 1
                    ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
                }`}
              >
                <FiChevronLeft className="w-4 h-4" />
                <span>Prev</span>
              </button>
              
              <span className="font-bold text-gray-700 px-2.5 py-1 bg-white border border-gray-200 rounded-xl shadow-xs">
                {currentPage} / {lastPage}
              </span>

              <button
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                disabled={currentPage >= lastPage}
                className={`p-1.5 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                  currentPage >= lastPage
                    ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
                }`}
              >
                <span>Next</span>
                <FiChevronRight className="w-4 h-4" />
              </button>

              <button
                onClick={onClose}
                className="ml-3 px-4 py-1.5 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold transition-colors"
              >
                Tutup
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DrugModal;
